import * as React from "react";
import { Button } from "../../components/ui/button";
import { Calendar as CalendarComponent } from "../../components/ui/calendar";
import { useState } from "react";

/**
 * A reusable date range selector component
 *
 * @param {Object} props
 * @param {Function} props.onApply - Callback when apply button is clicked with startDate and endDate
 * @param {boolean} props.loading - Whether the component is in loading state
 */
const DateRangeSelector = ({ onApply, loading = false }) => {
  // Internal state for date range selection
  const [dateRange, setDateRange] = useState("allTime");
  const [customRange, setCustomRange] = useState({ from: null, to: null });

  const dateRangeOptions = [
    { value: "allTime", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "Last 7 Days", label: "Last 7 Days" },
    { value: "1 Month", label: "1 Month" },
    { value: "custom", label: "Custom" },
  ];

  // Calculate date range based on selection
  const calculateDateRange = (rangeType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const ranges = {
      today: {
        from: today + 1,
        to: today + 1,
      },
      yesterday: {
        from: new Date(today.setDate(today.getDate() - 1)),
        to: new Date(today),
      },
      "Last 7 Days": {
        from: new Date(new Date().setDate(new Date().getDate() - 6)),
        to: new Date() - 1,
      },
      "1 Month": {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      },
      allTime: { from: null, to: null },
    };
    return ranges[rangeType] || { from: null, to: null };
  };

  // Handle date range selection
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range !== "custom") {
      setCustomRange(calculateDateRange(range));
    }
  };

  // Handle calendar selection
  const handleCalendarSelect = (range) => {
    setCustomRange(range || { from: null, to: null });
    setDateRange("custom");
  };

  // Format date for API
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Handle apply button click
  const handleApplyFilters = () => {
    // Convert selected dates to API format
    const startDate = customRange.from ? formatDate(customRange.from) : null;
    const endDate = customRange.to ? formatDate(customRange.to) : null;

    // Call the onApply callback with the formatted dates
    onApply({ startDate, endDate });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
      <h3 className="font-medium mb-3 text-sm sm:text-base">Date Range</h3>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Date Range Options */}
        <div className="w-full lg:min-w-[140px] lg:w-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2">
            {dateRangeOptions.map((option) => (
              <label
                key={option.value}
                className="inline-flex items-center p-2 sm:p-3 lg:p-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 text-xs sm:text-sm"
              >
                <input
                  type="radio"
                  checked={dateRange === option.value}
                  onChange={() => handleDateRangeChange(option.value)}
                  className="mr-2 flex-shrink-0"
                />
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="flex-1 overflow-x-auto">
          <CalendarComponent
            mode="range"
            selected={customRange}
            onSelect={handleCalendarSelect}
            className="rounded-md border mx-auto"
            numberOfMonths={
              typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 2
            }
          />
        </div>
      </div>

      <div className="flex justify-center sm:justify-end mt-4">
        <Button
          onClick={handleApplyFilters}
          disabled={loading}
          className="w-full sm:w-auto"
        >
          Apply Date Filter
        </Button>
      </div>
    </div>
  );
};

export default DateRangeSelector;
